import { Chip, ListSubheader, TextField, useTheme } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import match from 'autosuggest-highlight/match'
import parse from 'autosuggest-highlight/parse'
import { useKeywordsTranslation } from 'components/Common/ArticleSelector/useKeywordsTranslation'
import { SaveButton } from 'components/Common/Button/SaveButton'
import { MaterialsController } from 'controllers/MaterialsController'
import { useController } from 'customHooks/useController'
import { headerFilteredRowsSelector } from 'features/BillOfMaterials/store/selectors/headerFilteredRowsSelector'
import { ProjectEditableStateSelector } from 'features/BillOfMaterials/store/selectors/ProjectEditableStateSelector'
import { isEqual } from 'lodash'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from 'store/configureStore'
import { useAppDispatch } from 'store/configureStore'
import { KeywordDto } from 'services/APIs/InternalAPI/internal-api.contracts'
import { ShowException } from 'store/Application/appActions'
import { useOrganizationAndProjectContext } from 'utils/useOrganizationContext'
import './AssemblyKeywords.scss'
import { projectSelectors } from 'features/BillOfMaterials/store/selectors/projectSelectors'

type Props = {
  assemblyId: string
  disabled?: boolean
  onClose?: () => void
  hideLabel?: boolean
  // filteredPartTypeIds: string[]
}

const MaterialKeywordsOptions = (option, state) => {
  const matches = match(option.translatedString, state.inputValue)
  const parts = parse(option.translatedString, matches)

  return (
    <div>
      {parts.map((part, index) => (
        <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
          {part.text}
        </span>
      ))}
    </div>
  )
}

type KeywordWithTranslation = KeywordDto & { translatedString: string }

export const AssemblyKeywords = (props: Props & { active: boolean }) => {
  const { partyId, projectId } = useOrganizationAndProjectContext()
  const canBeEdited = useAppSelector(
    projectSelectors.canEditRowSelector(props.assemblyId)
  )
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const { asArrayOfTranslatedKeywords } = useKeywordsTranslation()

  const filteredPartTypeIds = useAppSelector(
    headerFilteredRowsSelector(props.assemblyId),
    isEqual
  )

  const { controller, loading } = useController(
    () => new MaterialsController(partyId, dispatch)
  )
  const [selectableKeywords, setSelectableKeywords] =
    useState<KeywordWithTranslation[]>(undefined)

  const [selectedKeywords, setSelectedKeywords] = useState<
    KeywordWithTranslation[]
  >([])
  const [open, setOpen] = useState(false)

  // const filteredPartTypes = useAppSelector((state: RootAppState) => (state.newProjectReducer.flatView[props.assemblyId] as AssemblyHeaderRow).filteredPartTypeIds)

  const { t } = useTranslation('common')

  const getSelectableKeywords = React.useCallback(
    async (selectedKeywords: KeywordDto[]) => {
      try {
        const keywords = await controller.GetSelectableKeywordsForAssembly(
          projectId,
          props.assemblyId,
          selectedKeywords.map((k) => k.originalKeyword)
        )

        setSelectableKeywords(asArrayOfTranslatedKeywords(keywords))
      } catch (err) {
        console.error(err)
        // ShowException('project', err)
      }
    },
    [asArrayOfTranslatedKeywords, controller, projectId, props.assemblyId]
  )

  React.useEffect(() => {
    // if (props.active) {
    //     getAvailableKeywords([])
    // }

    return () => {
      setSelectedKeywords([])
      setSelectableKeywords([])
    }
  }, [getSelectableKeywords, props.active])

  const handleRemoveKeywords = (token: KeywordWithTranslation) => {
    const newSelectedKeywords = selectedKeywords.filter(
      (x) => x.originalKeyword !== token.originalKeyword
    )

    setSelectedKeywords(newSelectedKeywords)
    getSelectableKeywords(newSelectedKeywords)
  }

  const handleSaveKeywords = async () => {
    try {
      setOpen(false)
      await controller.AddKeywordsToRows(projectId, {
        keywords: selectedKeywords,
        rowIds: filteredPartTypeIds,
      })
      setSelectedKeywords([])
      setSelectableKeywords(undefined)
      props.onClose && props.onClose()
    } catch (err) {
      ShowException('project', err)
    }
  }

  const projectIsEditable = useAppSelector(ProjectEditableStateSelector)

  if (!projectIsEditable)
    return (
      <div className={'assembly-keywords open assembly-keywords-selector'} />
    )

  return (
    <div className={`assembly-keywords ${props.active ? 'open' : 'closed'}`}>
      <div className="assembly-keywords-selector">
        <Autocomplete
          disableCloseOnSelect
          filterSelectedOptions
          multiple
          fullWidth
          open={open}
          onDoubleClick={(e) => e.stopPropagation()}
          autoHighlight
          autoComplete
          handleHomeEndKeys
          disableClearable
          onOpen={() => {
            setOpen(true)
            getSelectableKeywords(selectedKeywords)
          }}
          onClose={() => {
            setOpen(false)
          }}
          options={[
            ...(selectedKeywords || []),
            ...(selectableKeywords || []),
          ].sort((a, b) => a.category.localeCompare(b.category))}
          loading={loading['GetSelectableKeywordsForAssembly']}
          loadingText={`${t('common:loading')}...`}
          renderOption={MaterialKeywordsOptions}
          //t(`keywords:${option.keyword.id}`, { value: option.keyword.value })
          getOptionSelected={(option, value) => {
            return option.originalKeyword === value.originalKeyword
          }}
          getOptionLabel={
            (option) => option.translatedString
            //t(`keywords:${option.keyword.id}`, { value: option.keyword.value })
          }
          value={selectedKeywords || []}
          placeholder={'select keywords to apply to parts'}
          disabled={
            loading['AddKeywordsToRows'] ||
            props.disabled ||
            !filteredPartTypeIds?.length
          }
          // filterOptions={(options, { inputValue }) => {
          //   const selectable = options.filter((o) => o.category)

          //   if (!inputValue) return selectable

          //   return matchSorter(selectable, inputValue, {
          //     keys: ['translatedString'],
          //   })
          // }}
          onChange={(e, value) => {
            if (typeof value === 'string') {
              return
            }
            // setSelectedKeywords((value as { group: string; keyword: string; }[]).map(x => ({ group: '', keyword: x.keyword })))
            setSelectedKeywords(value as KeywordWithTranslation[])

            setSelectableKeywords([])
            getSelectableKeywords(value as KeywordWithTranslation[])
          }}
          groupBy={(option) => option.category}
          renderTags={(options, getTagProps) =>
            options.map((token, index) => {
              return (
                <Chip
                  key={index}
                  size="small"
                  variant="outlined"
                  label={token.translatedString}
                  {...getTagProps({ index })}
                  onDelete={() => handleRemoveKeywords(token)}
                />
              )
            })
          }
          renderGroup={(params) => {
            return [
              <ListSubheader
                key={params.key}
                style={{
                  zIndex: 2,
                  backgroundColor: theme.palette.background.paper,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  borderTop: `1px solid ${theme.palette.divider}`,
                }}
              >
                {t(`project:material-category-${params.group}`, params.group)}
              </ListSubheader>,
              params.children,
            ]
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              disabled={!canBeEdited}
              label={
                !props.hideLabel &&
                t('common:material-keywords', 'material keywords')
              }
              placeholder={
                selectedKeywords.length
                  ? ''
                  : t(
                      'project:keywords-apply-to-parts',
                      'apply material keywords to parts'
                    )
              }
              InputProps={{
                ...params.InputProps,
                endAdornment:
                  selectedKeywords.length > 0 ? (
                    <>
                      <SaveButton
                        translationKey="common:apply"
                        size="small"
                        saving={loading['AddKeywordsToRows']}
                        variant="text"
                        onClick={() => handleSaveKeywords()}
                        style={{ marginLeft: 'auto' }}
                      />
                      {params.InputProps.endAdornment}
                    </>
                  ) : (
                    params.InputProps.endAdornment
                  ),
              }}
              fullWidth
              // helperText={`${loading['GetSelectableKeywordsForAssembly'] ? t('common:loading') : ''}`}
            />
          )}
        />
      </div>
    </div>
  )
}
